import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData } from 'react-router-dom';
import { cn, EmptyState, Table, TableBody, TableCell, TableHeader, TableRow } from '@immo/ui-kit';
import { ColumnsTrigger, type ColumnPreference } from '@immo/components';
import type { DeletedCompanyRow } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { FilterBar } from '@/components/filter-bar';
import { useWorkspaceFilter } from '@/lib/list-view/use-workspace-filter';
import { CalendarIcon, CommercialIcon, MembersIcon, WorkspacesIcon } from '@/components/layout/icons';
import { deletedBySortValue, useDeletedByLabel } from '@/components/deleted-by';
import { OperatorColumnHead } from '@/components/table/operator-column-head';
import type { OperatorColumn } from '@/components/table/operator-column';
import { loadOperatorColumnPreference, OPERATOR_COLUMN_KEYS } from '@/components/table/operator-column-store';
import { useOperatorTable } from '@/components/table/use-operator-table';

export const deletedCompaniesLoaderParams = [] as const;

export type DeletedCompaniesLoaderData = {
  readonly items: ReadonlyArray<DeletedCompanyRow>;
  readonly columnPreference: ColumnPreference;
};

export async function deletedCompaniesLoader(): Promise<DeletedCompaniesLoaderData> {
  const [result, columnPreference] = await Promise.all([
    apiClient.adminCompanies.listDeleted({ query: {} }),
    loadOperatorColumnPreference(OPERATOR_COLUMN_KEYS.deletedCompanies),
  ]);

  if (!result.ok) {
    throw new Response('Failed to load deleted companies', { status: result.status });
  }

  return { items: result.body.items, columnPreference };
}

function useDeletedCompanyColumns(): ReadonlyArray<OperatorColumn<DeletedCompanyRow>> {
  const t = useTranslations('pages.admin.companies.deleted');
  const intl = useIntl();
  const deletedByLabel = useDeletedByLabel();

  return useMemo(
    () => [
      {
        id: 'name',
        label: t('columns.name'),
        icon: CommercialIcon,
        cellClassName: 'font-semibold text-text-primary',
        cell: (row) => row.name,
        sortValue: (row) => row.name,
      },
      {
        id: 'workspace',
        label: t('columns.workspace'),
        icon: WorkspacesIcon,
        cell: (row) => row.workspaceName ?? t('unresolved-workspace'),
        sortValue: (row) => row.workspaceName,
      },
      {
        id: 'deletedBy',
        label: t('columns.deleted-by'),
        icon: MembersIcon,
        headClassName: 'w-[180px]',
        cellClassName: 'max-w-[180px] truncate text-text-secondary',
        cell: (row) => <span title={deletedByLabel(row.deletedBy)}>{deletedByLabel(row.deletedBy)}</span>,
        sortValue: (row) => deletedBySortValue(deletedByLabel(row.deletedBy), row.deletedBy),
      },
      {
        id: 'deletedAt',
        label: t('columns.deleted-at'),
        icon: CalendarIcon,
        headClassName: 'w-[170px]',
        cellClassName: 'tabular-nums text-text-tertiary',
        cell: (row) => intl.formatDate(row.deletedAt, { dateStyle: 'short', timeStyle: 'short' }),
        sortValue: (row) => Date.parse(row.deletedAt),
      },
    ],
    [t, intl, deletedByLabel],
  );
}

export function DeletedCompaniesPage() {
  const t = useTranslations('pages.admin.companies.deleted');
  const { items, columnPreference } = useLoaderData() as DeletedCompaniesLoaderData;

  const filter = useWorkspaceFilter(items);
  const workspaceFilter = filter.workspaceId;

  const filtered = useMemo(
    () => items.filter((item) => workspaceFilter === null || item.workspaceId === workspaceFilter),
    [items, workspaceFilter],
  );

  const catalogue = useDeletedCompanyColumns();
  const table = useOperatorTable({
    rows: filtered,
    preferenceKey: OPERATOR_COLUMN_KEYS.deletedCompanies,
    catalogue,
    alwaysVisibleId: 'name',
    loadedPreference: columnPreference,
  });

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={filtered.length} icon={CommercialIcon} />} />
      <div className="flex h-full min-h-0 flex-col">
        <FilterBar
          pills={filter.pills ?? undefined}
          {...(filter.pills !== null ? { overflow: filter.overflow } : {})}
          trailing={<ColumnsTrigger {...table.columns.trigger} />}
        />
        {filtered.length === 0 ? (
          <div className="p-6">
            <EmptyState title={t('empty')} />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto" data-testid="deleted-companies-table-scroll">
            <Table flush>
              <TableHeader>
                <TableRow {...table.columns.headerRowProps}>
                  {table.columns.visibleColumns.map((column) => (
                    <OperatorColumnHead key={column.id} column={column} table={table} />
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {table.sorted.map((row) => (
                  <TableRow key={row.id}>
                    {table.columns.visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={cn(column.cellClassName, table.columns.bodyCellClassName(column.id))}
                        style={column.cellStyle}
                      >
                        {column.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  );
}
